<template>
  <section class="flex w-[548px] flex-col items-start gap-4 self-stretch">
    <div>
      <UITextSmMedium>{{ t("common.integratePopup.email.shareViaEmail") }}</UITextSmMedium>
      <UITextXsRegular>{{ t(`${i18nPath}.email.emailSubheader`) }}</UITextXsRegular>
    </div>
    <section class="flex items-start gap-4 self-stretch">
      <div
        class="card-template cursor-pointer"
        :class="{
          'border-active-blank':
            templateState.type === EMAIL_CAMPAIGN_TYPE.BLANK && !templateState.isDropdownSelected,
        }"
        @click="createBlankTemplate"
      >
        <div class="flex flex-col items-center justify-center gap-[1rem] self-stretch p-3">
          <div
            class="flex h-[120px] w-[220px] items-center justify-center gap-2 self-stretch rounded-lg bg-[#F2F4F7]"
          >
            <PlusIcon class="h-8 w-8"></PlusIcon>
          </div>
          <UITextSmMedium>{{ t("common.integratePopup.email.blankTemplate") }}</UITextSmMedium>
        </div>
      </div>
      <div
        class="card-template cursor-pointer"
        :class="{
          'border-active-blank':
            templateState.type === EMAIL_CAMPAIGN_TYPE.MARKETING &&
            !templateState.isDropdownSelected,
        }"
        @click="createEmailMarketingTemplate"
      >
        <div class="flex flex-col items-center justify-center gap-[1rem] self-stretch p-3">
          <TemplateIcon class="h-[120px] w-[220px]"></TemplateIcon>
          <UITextSmMedium>{{
            t("common.integratePopup.email.emailMarketingTemplates")
          }}</UITextSmMedium>
        </div>
      </div>
    </section>
    <section class="flex flex-col items-start gap-4 self-stretch">
      <div class="flex items-center gap-2 self-stretch">
        <div class="h-[1px] flex-1 bg-gray-200 text-gray-200"></div>
        <UITextSmMedium>{{ t("common.integratePopup.email.or") }}</UITextSmMedium>
        <div class="h-[1px] flex-1 bg-gray-200 text-gray-200"></div>
      </div>
    </section>
    <section class="flex w-full items-start self-stretch">
      <UISelect
        id="template-select"
        :value="selectedTemplate"
        :filterable="true"
        class="select-placeholder"
        :options="templateOptions"
        :placeholder="t('common.integratePopup.email.chooseTemplate')"
        @change="handleTemplateChange"
      />
    </section>
    <section class="mt-[238px] flex justify-end self-stretch">
      <UIButton
        id="emailRedirect"
        type="primary"
        :disabled="!templateState.type"
        :loading="loading"
        @click="redirectFromContinue"
        >{{ t("common.integratePopup.email.continue") }}</UIButton
      >
    </section>
  </section>
</template>

<script setup lang="ts">
import TemplateIcon from "@/assets/icons/email-template.svg";
import config from "@/config";
import { EmailService } from "@/service/EmailServices";
import { useAppStore } from "@/store/app";
import { openLinkInNewTab } from "@/util/methods";
import { PlusIcon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UISelect, UITextSmMedium, UITextXsRegular } from "@gohighlevel/ghl-ui";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { EMAIL_CAMPAIGN_TYPE } from "../../../util/constants";

const store = useAppStore();
const { t } = useI18n();

const props = defineProps({
  widgetId: {
    type: String,
    required: true,
  },
});
const templateOptions = ref([]);
const selectedTemplate = ref();
const loading = ref(false);

// Replace individual refs with a single reactive state object
const templateState = ref({
  type: "", // 'blank' or 'marketing' or ''
  campaignData: null,
  isDropdownSelected: false,
});

getEmail();

const i18nPath = computed(() => {
  return store.formId
    ? "form.integratePopup"
    : store.surveyId
    ? "survey.integratePopup"
    : "quiz.integratePopup";
});
async function getEmail() {
  const response = await EmailService.getEmailList({
    locationId: store.locationId,
  });
  if (response.data.length) {
    templateOptions.value = response.data.map((template) => {
      return {
        label: template.name,
        value: template._id,
      };
    });
  }
}

const handleTemplateChange = async (templateId) => {
  templateState.value = {
    type: EMAIL_CAMPAIGN_TYPE.TEMPLATE,
    campaignData: null,
    isDropdownSelected: !!templateId,
  };
  selectedTemplate.value = templateId;
};

const createBlankTemplate = async () => {
  if (templateState.value.type !== EMAIL_CAMPAIGN_TYPE.BLANK) {
    templateState.value = {
      type: EMAIL_CAMPAIGN_TYPE.BLANK,
      campaignData: null,
      isDropdownSelected: false,
    };
    selectedTemplate.value = null;
  }
};

const createEmailMarketingTemplate = async () => {
  templateState.value = {
    type: EMAIL_CAMPAIGN_TYPE.MARKETING,
    campaignData: { campaignId: EMAIL_CAMPAIGN_TYPE.MARKETING, formId: props.widgetId },
    isDropdownSelected: false,
  };
  selectedTemplate.value = null;
};

const createCampaign = async (type) => {
  loading.value = true;
  try {
    const campaign = await EmailService.createCampaign({
      locationId: store.locationId,
      templateId: selectedTemplate.value ? selectedTemplate.value : "",
      formId: props.widgetId,
    });
    if (campaign.data) {
      templateState.value.type = type;
      templateState.value.campaignData = campaign.data;
    }
    loading.value = false;
  } catch (err) {
    loading.value = false;
    console.error(err);
  }
};

const redirectToEmailMarketing = () => {
  const url = `https://${
    store.company.domain ? store.company.domain : config.GHL_DOMAIN
  }/v2/location/${store.locationId}/marketing/emails/scheduled?formId=${
    props.widgetId
  }&pageNumber=1`;
  openLinkInNewTab(url);
};

const redirectToEmailBuilder = () => {
  const url = `https://${
    store.company.domain ? store.company.domain : config.GHL_DOMAIN
  }/location/${store.locationId}/emails/campaigns/create/${
    templateState.value.campaignData.campaignId
  }?activeElement=${templateState.value.campaignData.formId}`;
  openLinkInNewTab(url);
};

const redirectFromContinue = async () => {
  if (
    templateState.value.type === EMAIL_CAMPAIGN_TYPE.BLANK ||
    templateState.value.type === EMAIL_CAMPAIGN_TYPE.TEMPLATE
  ) {
    await createCampaign(templateState.value.type);
    redirectToEmailBuilder();
  }

  if (templateState.value.type === EMAIL_CAMPAIGN_TYPE.MARKETING) {
    return redirectToEmailMarketing();
  }
};
</script>

<style>
.card-template {
  display: flex;
  padding: 0px 2px;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  border-radius: 12px;
  border: 2px solid transparent;
  background: #fff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}

.card-template:not(.border-active-blank) {
  border-color: #eaecf0;
}

.border-active-blank {
  border-color: #155eef !important;
  background: #f8fafd;
}
</style>

<style scoped>
.select-placeholder :deep(.n-base-selection-overlay__wrapper) {
  color: #667085;
}
</style>
